import { buildMeasuresOverTimeQuickLink } from '../../../../helpers/flora-quick-links/flora-quick-links';
import type { QuickLink } from '../../../../helpers/flora-quick-links/flora-quick-links.type';
import type { MeasureComparisonPayload, MeasureDetail } from '../measure-comparison/measure-comparison-artifact.type';

interface MeasuresOverTimeDataset {
  key: string;
  label: string;
  data: Array<number | null>;
  units: string;
}

interface MeasuresOverTimeWorkPeriod {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  labels: string[];
  datasets: MeasuresOverTimeDataset[];
}

interface MeasuresOverTimePayload {
  work_periods: MeasuresOverTimeWorkPeriod[];
  measure_details?: MeasureDetail[];
}

interface MeasuresOverTimeBoardConfig {
  id: string;
  name: string;
  work_period_ids?: string[];
}

/**
 * Checks whether an artifact data object has the expected Measures-over-Time wrapper shape.
 * Requires not just a `work_periods` array but that every entry carries a `labels` array —
 * otherwise downstream rendering (`buildWorkPeriodComparisonPayload`) would crash on
 * `labels.map(...)`.
 *
 * @param {Record<string, unknown>} data - Raw Flora artifact data payload.
 * @returns {boolean} True when the payload exposes a well-formed work_periods array.
 */
function isMeasuresOverTimePayload(data: Record<string, unknown>): boolean {
  return (
    Array.isArray(data.work_periods) &&
    data.work_periods.every(
      (workPeriod) =>
        workPeriod != null &&
        typeof workPeriod === 'object' &&
        Array.isArray((workPeriod as Record<string, unknown>).labels),
    )
  );
}

/**
 * Builds the "Day N" x-axis label for a zero-based day index.
 *
 * @param {number} index - Zero-based index of the day within the work period.
 * @returns {string} Display label, e.g. "Day 1" for index 0.
 */
function buildDayLabel(index: number): string {
  return `Day ${index + 1}`;
}

/**
 * Maps one work period into a `MeasureComparisonPayload` so it can be rendered
 * by the shared `SingleObjectChart` (line / bar / table). The per-day calendar
 * dates become "Day 1..N" labels on the x-axis; the datasets and measure
 * metadata pass through unchanged.
 *
 * @param {MeasuresOverTimeWorkPeriod} workPeriod - The selected work period payload.
 * @param {MeasureDetail[] | undefined} measureDetails - Optional measure metadata for labels/units/trend.
 * @returns {MeasureComparisonPayload} A time-series payload for `SingleObjectChart`.
 */
function buildWorkPeriodComparisonPayload(
  workPeriod: MeasuresOverTimeWorkPeriod,
  measureDetails?: MeasureDetail[],
): MeasureComparisonPayload {
  return {
    scope: 'time_series',
    labels: workPeriod.labels.map((_, index) => buildDayLabel(index)),
    datasets: workPeriod.datasets,
    measure_details: measureDetails,
  };
}

/**
 * Builds quick links scoped to the currently selected Measures-over-Time work period.
 *
 * @param {Record<string, unknown>} data - Raw artifact data used by the quick-link builder.
 * @param {MeasuresOverTimeWorkPeriod} workPeriod - Currently selected work period.
 * @param {Record<string, unknown> | undefined} config - Raw artifact config containing board context.
 * @returns {QuickLink[]} Quick links scoped to the selected work period.
 */
function buildScopedMeasuresOverTimeQuickLinks(
  data: Record<string, unknown>,
  workPeriod: MeasuresOverTimeWorkPeriod,
  config?: Record<string, unknown>,
): QuickLink[] {
  const scopedConfig = config ? { ...config } : {};
  const rawBoards = scopedConfig.boards;
  const boards = Array.isArray(rawBoards) ? (rawBoards as MeasuresOverTimeBoardConfig[]) : [];
  const scopedBoards = boards.map((board) => ({
    ...board,
    work_period_ids: board.work_period_ids?.includes(workPeriod.id) ? [workPeriod.id] : board.work_period_ids,
  }));

  return buildMeasuresOverTimeQuickLink(data, { ...scopedConfig, boards: scopedBoards });
}

export {
  buildDayLabel,
  buildScopedMeasuresOverTimeQuickLinks,
  buildWorkPeriodComparisonPayload,
  isMeasuresOverTimePayload,
};
export type { MeasuresOverTimePayload, MeasuresOverTimeWorkPeriod };
